import React, { useRef } from 'react';
import classNames from 'classnames';
import { Link } from 'react-scroll';
import { useIsMobile, BREAKPOINTS } from '@buyside/util/hooks/useIsMobile';

import FundLetterList from '@buyside/components/presentation/FundLetterIndex/FundLetters/fundLetterList';
import FundLetterIndexLoading from '@buyside/components/presentation/FundLetterIndex/fundLetterIndexLoading';

import { Maybe, useFundLetterSourcesQuery } from '@interfaces/graphql-typings';

import styles from './fundLetterStyles.scss';

const sectionKeyList = '#ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const FundLetters: React.FC = (): React.ReactElement => {
  const navPaneRef = useRef<Maybe<HTMLDivElement>>(null);
  const isMobile = useIsMobile(BREAKPOINTS.LARGE);

  const { data, error, loading } = useFundLetterSourcesQuery();

  if (error) throw error;

  const fundLetterSortedSources: { [key: string]: string[] } =
    data?.fundLetterSources.reduce((acc: { [key: string]: string[] }, ele) => {
      const { startingCharacter, sources } = ele;
      if (sectionKeyList.includes(startingCharacter)) acc[startingCharacter] = sources;
      return acc;
    }, {}) || {};

  return (
    <>
      <h3>Fund Letters</h3>
      <div className={styles.contentContainer}>
        <div ref={navPaneRef} className={styles.navPane} data-testid="nav-pane">
          {sectionKeyList.split('').map(sectionKey => {
            const validSection = Boolean(fundLetterSortedSources[sectionKey]);
            return validSection ? (
              <Link
                className={classNames(styles.navLink, styles.valid)}
                isDynamic
                key={`${sectionKey}-link`}
                offset={
                  isMobile ? -50 : navPaneRef.current ? -navPaneRef.current.offsetHeight - 50 : 0
                }
                smooth
                spy
                to={`${sectionKey}-anchor`}
              >
                {sectionKey}
              </Link>
            ) : (
              <div className={styles.navLink} key={`${sectionKey}-link`}>
                {sectionKey}
              </div>
            );
          })}
        </div>
        <div className={styles.navAnchorContainer} data-testid="nav-anchors">
          {loading ? (
            <FundLetterIndexLoading isMobile={isMobile} />
          ) : (
            <>
              {Object.keys(fundLetterSortedSources).map((sectionKey: string) => {
                const listKey = `${sectionKey}-anchor`;
                return (
                  <div key={listKey} id={listKey} className={styles.navAnchor}>
                    <div className={styles.headerContainer}>
                      <div className={styles.header}>{sectionKey}</div>
                    </div>
                    <div className={styles.listContainer}>
                      {fundLetterSortedSources[sectionKey].map((fundName: string) => (
                        <FundLetterList fundName={fundName} key={fundName} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default FundLetters;
